import React, { useState } from 'react';
import { QStack, QSwitch, QText, useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import DOMPurify from 'dompurify';
import { patchCompanyFeatures } from '../../../../api/medtech-api';
import { Features } from '../../../../types/features.types';

type BetaFeaturesControlProps = {
  featureKey: string;
  name: string;
  description: string;
  active: boolean;
};

const HtmlDescription: React.FC<{ description: string }> = ({ description }) => {
  const sanitizedHtml = DOMPurify.sanitize(description, {
    ADD_ATTR: ['target', 'rel'],
  });
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, 'text/html');
  const links = doc.querySelectorAll('a');

  links.forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  return (
    <QText color="gray.500" fontSize="sm" maxWidth="720px" dangerouslySetInnerHTML={{ __html: doc.body.innerHTML }} />
  );
};

export const BetaFeaturesControl = ({ featureKey, name, description, active }: BetaFeaturesControlProps) => {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const [isChecked, setIsChecked] = useState(active);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToastProvider();
  const { t } = useTranslation();

  // `isChecked` is initialised from the `active` prop, which is read from this cached
  // features list. Update the cache alongside the toggle so the two don't drift if the
  // list is re-rendered from cache.
  const updateCachedFeature = (featureActive: boolean) => {
    queryClient.setQueryData<Features | undefined>(['company-features', currentUser.companyId], (previous) =>
      previous
        ? {
            ...previous,
            features: previous.features.map((feature) =>
              feature.key === featureKey ? { ...feature, active: featureActive } : feature,
            ),
          }
        : previous,
    );
  };

  const handleToggleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isEnabled = event.target.checked;
    // Drive the controlled switch from the user's intent: set it synchronously here,
    // keep it on success, and revert only if the request fails.
    setIsChecked(isEnabled);
    updateCachedFeature(isEnabled);

    try {
      setIsLoading(true);
      await patchCompanyFeatures(currentUser.companyId, {
        flag_key: featureKey,
        flag_title: name,
        enable: isEnabled,
      });

      const message = isEnabled ? t('betaFeatures.enabled', { name }) : t('betaFeatures.disabled', { name });
      showToast({
        'data-cy': 'beta-features-toggle-success-toast',
        id: 'patch-features',
        replace: true,
        description: message,
        status: 'success',
      });
    } catch (error) {
      setIsChecked(!isEnabled);
      updateCachedFeature(!isEnabled);
      showToast({
        'data-cy': 'beta-features-toggle-error-toast',
        id: 'patch-features',
        replace: true,
        description: t('messages.error.updateFeatures'),
        status: 'error',
      });
    }
    setIsLoading(false);
  };

  return (
    <QStack spacing="4">
      <QStack spacing="2">
        <QText fontWeight="semibold" fontSize="md">
          {name}
        </QText>
        <HtmlDescription description={description} />
        <QSwitch
          data-cy={`${featureKey}-switch`}
          isChecked={isChecked}
          onChange={handleToggleChange}
          rightLabel={t('betaFeatures.enable', { name })}
          isDisabled={isLoading}
        />
      </QStack>
    </QStack>
  );
};
